<template>
  <div
    class="sf-header"
    :class="{ 'is-sticky': sticky, 'is-hidden': hidden }">
    <div class="sf-header__wrapper">
      <header
        ref="header"
        class="sf-header__header page-max-content-width">
        <div class="header_left ml-3">
          <PtHeaderLeft />
          <div
            class="mobile-menu-icon mobile-only "
            @click="loadCategoryMenu">
            <img
              :src="require('@/assets/icons/menu.svg')"
              alt="open menu">
          </div>
        </div>
        <div
          v-if="logoWidth && logoHeight"
          :style="{ maxHeight: logoHeight + 'px', width: logoWidth + 'px' }"
          class="pt-header__logo">
          <slot
            name="logo"
            v-bind="{ logo, title }">
            <SfLink link="/">
              <SfImage
                v-if="logo"
                :src="logo"
                :alt="title"
                :width="logoWidth"
                :height="logoHeight"
                class="sf-header__logo" />
              <h1
                v-else
                class="sf-header__title">
                {{ title }}
              </h1>
            </SfLink>
          </slot>
        </div>

        <div class="header_right">
          <div class="search_bar">
            <div class="sf-header__aside">
              <slot name="aside" />
            </div>
            <div class="pt-header__search">
              <slot
                name="search"
                v-bind="{ searchValue, searchPlaceholder }">
                <SfSearchBar
                  :value="searchValue"
                  :placeholder="searchPlaceholder"
                  aria-label="Search"
                  class="sf-header__search"
                  @input="$emit('change:search', $event)"
                  @keyup.enter="$emit('enter:search', $event)" />
              </slot>
            </div>
          </div>
          <div class="sf-header__actions">
            <slot
              name="header-icons"
              v-bind="{
                activeIcon,
                cartHasProducts,
                cartItemsQty,
                cartIcon,
                wishlistIcon,
                accountIcon,
              }">
              <div class="sf-header__icons">
                <SfButton
                  :class="{ 'display-none': !accountIcon }"
                  class="sf-button--pure sf-header__action"
                  data-testid="accountIcon"
                  :aria-label="'Account'"
                  @click="$emit('click:account')">
                  <SfIcon
                    :icon="accountIcon"
                    size="1.25rem"
                    :class="{
                      'sf-header__icon is-active': activeIcon === 'account',
                    }" />
                </SfButton>
                <SfButton
                  :class="{ 'display-none': !wishlistIcon }"
                  class="sf-button--pure sf-header__action"
                  data-testid="wishlistIcon"
                  :aria-label="'Wishlist'"
                  @click="$emit('click:wishlist')">
                  <SfIcon
                    class="sf-header__icon"
                    :icon="wishlistIcon"
                    size="1.25rem"
                    :class="{
                      'sf-header__icon is-active': activeIcon === 'wishlist',
                    }" />
                </SfButton>
                <SfButton
                  :class="{ 'display-none': !cartIcon }"
                  class="sf-button--pure sf-header__action"
                  data-testid="cartIcon"
                  :aria-label="'Cart'"
                  @click="$emit('click:cart')">
                  <SfIcon
                    class="sf-header__icon"
                    :icon="cartIcon"
                    size="1.25rem"
                    :class="{
                      'sf-header__icon is-active': activeIcon === 'cart',
                    }" />
                </SfButton>
              </div>
            </slot>
          </div>
        </div>
      </header>
      <!--   header navigation menu   -->
      <div class="pt-header_navigation">
        <nav
          class="pt-header__navigation"
          :class="{ 'is-visible': isNavVisible }">
          <slot name="navigation" />
        </nav>
      </div>
      <!--   header navigation menu   -->
    </div>
  </div>
</template>
<script>
import PtHeaderLeft from '~/components/Pointeger/atoms/PtHeaderLeft.vue';
import Vue from 'vue';
import SfHeaderNavigationItem
  from '@storefront-ui/vue/src/components/organisms/SfHeader/_internal/SfHeaderNavigationItem.vue';
import SfHeaderNavigation from '@storefront-ui/vue/src/components/organisms/SfHeader/_internal/SfHeaderNavigation.vue';
import { isClient } from '@storefront-ui/vue/src/utilities/helpers/check-environment';
import {
  SfImage, SfSearchBar, SfButton, SfIcon, SfLink,
} from '@storefront-ui/vue';

Vue.component('SfHeaderNavigation', SfHeaderNavigation);
Vue.component('SfHeaderNavigationItem', SfHeaderNavigationItem);
export default {
  name: 'SfHeader',
  components: {
    PtHeaderLeft,
    SfImage,
    SfSearchBar,
    SfButton,
    SfIcon,
    SfLink,
  },
  props: {
    logo: {
      type: [String, Object],
      default: '',
    },
    logoHeight: {
      type: [Number, String],
      default: 35,
    },
    logoWidth: {
      type: [Number, String],
      default: 34,
    },
    title: {
      type: String,
      default: '',
    },
    cartIcon: {
      type: [String, Boolean, Array],
      default: 'empty_cart',
    },
    wishlistIcon: {
      type: [String, Boolean, Array],
      default: 'heart',
    },
    accountIcon: {
      type: [String, Boolean, Array],
      default: 'profile',
    },
    activeIcon: {
      type: String,
      default: '',
      validator(value) {
        return ['', 'account', 'wishlist', 'cart'].includes(value);
      },
    },
    searchPlaceholder: {
      type: String,
      default: 'Search for items',
    },
    searchValue: {
      type: String,
      default: '',
    },
    wishlistItemsQty: {
      type: [String, Number],
      default: '0',
    },
    cartItemsQty: {
      type: [String, Number],
      default: '0',
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    isNavVisible: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const loadCategoryMenu = () => {
      if (document) {
        const mobileNav = document.querySelector('[data-testid="bottom-navigation-menu"]');
        if (mobileNav) {
          mobileNav.click();
        }
      }
    };
    return {
      loadCategoryMenu,
    };
  },
  data() {
    return {
      icons: [],
      hidden: false,
      sticky: false,
      scrollDirection: null,
      lastScrollPosition: 0,
      animationStart: null,
      animationLong: null,
      animationDuration: 300,
    };
  },
  computed: {
    cartHasProducts() {
      return Number.parseInt(this.cartItemsQty, 10) > 0;
    },
    wishlistHasProducts() {
      return Number.parseInt(this.wishlistItemsQty, 10) > 0;
    },
  },
  watch: {
    scrollDirection: {
      handler() {
        if (!isClient) return;
        window.cancelAnimationFrame(this.animationLong);
        this.animationLong = null;
        this.animationStart = null;
        this.animationLong = window.requestAnimationFrame(
          this.animationHandler,
        );
      },
    },
    isSticky: {
      handler(isSticky) {
        if (!isClient) return;
        this.sticky = isSticky;
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.isSticky) {
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  },
  beforeDestroy() {
    if (this.isSticky) {
      window.removeEventListener('scroll', this.scrollHandler, {
        passive: true,
      });
    }
  },
  methods: {
    animationHandler(timestamp) {
      if (!this.animationStart) this.animationStart = timestamp;
      const progress = timestamp - this.animationStart;
      if (progress < this.animationDuration) {
        this.animationLong = window.requestAnimationFrame(
          this.animationHandler,
        );
        return;
      }
      this.hidden = this.scrollDirection === 'down';
    },
    scrollHandler() {
      if (!isClient) return;
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      if (!!this.refs && currentScrollPosition >= this.$refs.header.offsetHeight) {
        this.scrollDirection = currentScrollPosition < this.lastScrollPosition ? 'up' : 'down';
      }
      this.lastScrollPosition = currentScrollPosition;
    },
  },

};
</script>
<style lang="scss" scoped>
@import "assets/css/my-mixins.scss";
@import "~@storefront-ui/shared/styles/components/organisms/SfHeader.scss";

.sf-header__header {
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  position: relative;
  border-bottom: unset;
  box-shadow: unset;
  @include for-mobile-1024-down {
    padding: 10px;
    margin-bottom: 0;
    border-bottom: 1px solid gray;
  }
  .sf-header__actions {
    flex: none;
  }

  .search_bar {
    width: 100%;
  }
  .pt-header__search {
    .sf-header__search {
      max-width: 500px;
    }
  }
}

.pt-header__logo{
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @include for-mobile-1024-down {
    width: 4rem;
    position: unset;
    transform: translateX(0);
  }
}

.header_left {
  width: 25%;
  display: flex;

  @include for-mobile-1024-down {
    width: 25px;
    height: 25px;
  }

  .mobile-menu-icon {
    display: none;
    @include for-mobile-1024-down {
      display: block;
    }
  }
}

.header_right {
  display: flex;
  align-items: center;
  @include for-mobile-1024-down {
    .search_bar {

      .pt-header__search {

        .sf-search-bar {
          width: 100%;

          .sf-input__wrapper{
            display: none;
            input{
              opacity: 0;
            }
          }
        }
      }
    }
  }
}

.pt-header__search {
  .sf-input.sf-search-bar.sf-header__search {
    @include for-desktop {
      height: 30px;
      background-color: var(--color-white);
      border-bottom: 1px solid var(--color-light-grey);
    }
  }
}

.pt-header_navigation {
  background-color: var(--color-white);

  .pt-header__navigation {
    border-top: 1px solid var(--color-light-grey);
    border-bottom: 1px solid var(--color-light-grey);
    position: relative;
  }
}

.sf-header.sf-header--has-mobile-search {
  .sf-header__wrapper {
    background-color: var(--color-light-hot-grey);

    .pt-header_navigation {
      @include for-mobile-1024-down {
        display: none;
      }
    }
  }
}
</style>
